import { AccountInfo, EventType, PublicClientApplication } from "@azure/msal-browser";
import { checkLoggedIn, msalConfig, useLogin, isUsingAppServicesLogin, identityProviders } from "./authConfig";
import { useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { LoginContext } from "./loginContext";
import Layout from "./pages/layout/Layout";
import LoginSelection from "./pages/login/LoginSelection";

/**
 * LayoutWrapper is a functional component responsible for managing authentication
 * state and providing it to the application's layout via context. It determines
 * whether to use Microsoft Authentication Library (MSAL) for managing user sessions
 * and handles setting up the authentication instance and managing logged-in state.
 *
 * If authentication is enabled, it creates an instance of PublicClientApplication with
 * a given configuration, selects an active account if none is set, and listens for
 * successful login events to update the active account. It also fetches the current
 * login state asynchronously and updates the component state accordingly.
 *
 * When using Easy Auth with multiple identity providers (Microsoft + Salesforce),
 * shows a login selection page if the user is not yet authenticated.
 *
 * Provides:
 * - An authentication context using MSAL when enabled.
 * - A login state context to child components to determine and update login status.
 *
 * Returns:
 * - A React component that wraps children with necessary providers for authentication
 *   and layout management.
 */
const LayoutWrapper = () => {
    const [loggedIn, setLoggedIn] = useState(false);

    // Easy Auth mode: check if user is already authenticated via App Service
    const isAuthenticated = isUsingAppServicesLogin;

    if (useLogin) {
        var msalInstance = new PublicClientApplication(msalConfig);

        // Default to using the first account if no account is active on page load
        if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
            // Account selection logic is app dependent. Adjust as needed for different use cases.
            msalInstance.setActiveAccount(msalInstance.getActiveAccount());
        }

        // Listen for sign-in event and set active account
        msalInstance.addEventCallback(event => {
            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                const account = event.payload as AccountInfo;
                msalInstance.setActiveAccount(account);
            }
        });

        useEffect(() => {
            const fetchLoggedIn = async () => {
                setLoggedIn(await checkLoggedIn(msalInstance));
            };

            fetchLoggedIn();
        }, []);

        return (
            <MsalProvider instance={msalInstance}>
                <LoginContext.Provider
                    value={{
                        loggedIn,
                        setLoggedIn
                    }}
                >
                    <Layout />
                </LoginContext.Provider>
            </MsalProvider>
        );
    }

    // Easy Auth mode: show login selection if not authenticated
    if (!isAuthenticated) {
        return (
            <LoginSelection
                microsoftLoginUrl={identityProviders.microsoft.loginUrl}
                salesforceLoginUrl={identityProviders.salesforce.loginUrl}
            />
        );
    }

    // Authenticated via Easy Auth — render the app
    return (
        <LoginContext.Provider
            value={{
                loggedIn: true,
                setLoggedIn
            }}
        >
            <Layout />
        </LoginContext.Provider>
    );
};

export default LayoutWrapper;
